import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import axios from "axios";
import { 
  Users, Calendar, CreditCard, Mail, UserPlus, MessageSquare,
  TrendingUp, Menu, X, BarChart3, Activity, Bot, Megaphone
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  
  const links = [
    { href: "/admin", label: "Resumen", icon: TrendingUp },
    { href: "/admin/listeners", label: "Listeners", icon: Users },
    { href: "/admin/usuarios", label: "Usuarios", icon: Users },
    { href: "/admin/sesiones", label: "Sesiones", icon: Calendar },
    { href: "/admin/invitaciones", label: "Invitaciones", icon: Mail },
    { href: "/admin/solicitudes", label: "Solicitudes", icon: MessageSquare },
    { href: "/admin/analytics", label: "Analytics & Headlines", icon: BarChart3 },
    { href: "/admin/tracking", label: "Tracking (Pixel/GA)", icon: Activity },
    { href: "/admin/chatbot", label: "Chatbot", icon: Bot },
    { href: "/admin/promotores", label: "Promotores", icon: Megaphone },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white lg:bg-transparent
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:shrink-0 lg:block
        border-r lg:border-r-0 border-stone-200
        pt-4 lg:pt-0
      `}>
        {/* Mobile close button */}
        <div className="flex justify-end p-4 lg:hidden">
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <nav className="space-y-1 px-4 lg:px-0">
          {links.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              onClick={onClose}
              className={`sidebar-link ${location.pathname === link.href ? 'active' : ''}`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
};

export const AdminLayout = ({ children, title }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  return (
    <Layout showFooter={false}>
      <div className="container-main py-4 lg:py-8">
        {/* Mobile header with menu button */}
        <div className="flex items-center gap-4 mb-4 lg:hidden">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-stone-100 rounded-lg"
            aria-label="Abrir menú"
          >
            <Menu className="w-6 h-6" />
          </button>
          {title && (
            <h1 className="text-xl font-serif font-bold text-foreground">{title}</h1>
          )}
        </div>
        
        <div className="flex gap-8">
          <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
          <main className="flex-1 min-w-0 w-full">
            {title && (
              <h1 className="hidden lg:block text-2xl font-serif font-bold text-foreground mb-6">{title}</h1>
            )}
            {children}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    listeners: 0,
    users: 0,
    sessions: 0,
    pendingProfiles: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [listenersRes, usersRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/admin/listeners`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/sessions`)
      ]);
      
      // Handle both old array response and new paginated response
      const listeners = Array.isArray(listenersRes.data) ? listenersRes.data : listenersRes.data.listeners || [];
      const users = Array.isArray(usersRes.data) ? usersRes.data : usersRes.data.users || [];
      const sessions = Array.isArray(sessionsRes.data) ? sessionsRes.data : sessionsRes.data.sessions || [];
      const usersTotal = usersRes.data.total || users.length;
      const sessionsTotal = sessionsRes.data.total || sessions.length;
      
      setStats({
        listeners: listeners.length,
        users: usersTotal,
        sessions: sessionsTotal,
        pendingProfiles: listeners.filter(l => l.status === 'pending').length
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-serif font-bold text-foreground">Panel de administración</h1>
          <p className="mt-1 text-stone-600">Bienvenido/a, {user?.name}</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Listeners</p>
                    <p className="text-2xl font-bold text-foreground">{stats.listeners}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Usuarios</p>
                    <p className="text-2xl font-bold text-foreground">{stats.users}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Sesiones</p>
                    <p className="text-2xl font-bold text-foreground">{stats.sessions}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-500">Perfiles pendientes</p>
                    <p className="text-2xl font-bold text-foreground">{stats.pendingProfiles}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Acciones rápidas</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link 
                  to="/admin/invitaciones" 
                  className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                  data-testid="invite-listener-action"
                >
                  <Mail className="w-5 h-5 text-primary" />
                  <span className="font-medium text-foreground">Invitar listener</span>
                </Link>
                <Link 
                  to="/admin/listeners" 
                  className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                  data-testid="manage-listeners-action"
                >
                  <Users className="w-5 h-5 text-primary" />
                  <span className="font-medium text-foreground">Gestionar listeners</span>
                </Link>
                <Link 
                  to="/admin/solicitudes" 
                  className="flex items-center gap-3 p-4 bg-stone-50 rounded-xl hover:bg-stone-100 transition-colors"
                  data-testid="view-requests-action"
                >
                  <MessageSquare className="w-5 h-5 text-primary" />
                  <span className="font-medium text-foreground">Ver solicitudes</span>
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
